import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {clsx} from 'clsx'
import {
  calculateDiffLineCodeCellGutter,
  cellIdFrom,
  getDiffLineMarkersKey,
  getLineBackgroundColor,
  rowIdFrom,
  trimDiffLineHtml,
} from '../../helpers/line-helpers'
import type {DiffLine, LineKey} from '../../types'
import {useMarkersAtPosition} from '@github-ui/pull-requests/pull-request-store-selectors'
import {InlineMarkers} from '../conversations/InlineMarkers'
import {noop} from '@github-ui/noop'
import {StartConversation} from '../conversations/StartConversation'
import {useRef, useState} from 'react'
import {SimpleActionBar} from '../grid-cells/SimpleActionBar'
import {useDiffAnchor} from '../../stores/selectors'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {ViewerData} from '../../conversation-types'

const leftColumnIndex = 1
const rightColumnIndex = 3

export function SimpleSplitDiffRow({
  leftDiffLine,
  rightDiffLine,
  filePath,
  avatarURL,
  userLogin,
  viewerCanApplySuggestion,
}: {
  leftDiffLine: DiffLine | undefined
  rightDiffLine: DiffLine | undefined
  filePath: string
  avatarURL?: string
  userLogin?: string
  viewerCanApplySuggestion?: boolean
}) {
  const isHunkRow = leftDiffLine?.type === 'HUNK'
  const diffAnchor = useDiffAnchor()

  // TODO: line selection should be different color in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const leftLineNumberBackgroundColor = leftDiffLine ? getLineBackgroundColor(leftDiffLine.type, true, false) : ''
  const rightLineNumberBackgroundColor = rightDiffLine ? getLineBackgroundColor(rightDiffLine.type, true, false) : ''
  const leftCodeBackgroundColor = leftDiffLine ? getLineBackgroundColor(leftDiffLine.type, false, false) : ''
  const rightCodeBackgroundColor = rightDiffLine ? getLineBackgroundColor(rightDiffLine.type, false, false) : ''
  const rowId = leftDiffLine ? rowIdFrom(diffAnchor, leftDiffLine, rightDiffLine) : ''
  const leftCellId = cellIdFrom(rowId, leftColumnIndex)
  const rightCellId = cellIdFrom(rowId, rightColumnIndex)

  const [leftLineHtml, leftLineTypeCharacter] = leftDiffLine
    ? trimDiffLineHtml(leftDiffLine.html, leftDiffLine.type)
    : [undefined, undefined]
  const showLeftLineTypeCharacter = leftLineTypeCharacter && ['+', '-'].includes(leftLineTypeCharacter)

  const [rightLineHtml, rightLineTypeCharacter] = rightDiffLine
    ? trimDiffLineHtml(rightDiffLine.html, rightDiffLine.type)
    : [undefined, undefined]
  const showRightLineTypeCharacter = rightLineTypeCharacter && ['+', '-'].includes(rightLineTypeCharacter)

  const leftLineMarkersKey = leftDiffLine ? getDiffLineMarkersKey(leftDiffLine.left, 'LEFT') : undefined
  const {threads: leftThreads, annotations: leftAnnotations} = useMarkersAtPosition(
    filePath,
    leftLineMarkersKey ?? ('' as LineKey),
  )

  const viewerData = {
    avatarUrl: avatarURL ?? '',
    login: userLogin ?? '',
    viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
  }
  const rightLineMarkersKey = rightDiffLine ? getDiffLineMarkersKey(rightDiffLine.right, 'RIGHT') : undefined
  const {
    threads: rightThreads,
    annotations: rightAnnotations,
    commentsPreference,
  } = useMarkersAtPosition(filePath, rightLineMarkersKey ?? ('' as LineKey))
  const [isLeftNewConversationDialogOpen, setIsLeftNewConversationDialogOpen] = useState(false)
  const [isLeftActionBarVisible, setIsLeftActionBarVisible] = useState(false)
  const [isRightNewConversationDialogOpen, setIsRightNewConversationDialogOpen] = useState(false)
  const [isRightActionBarVisible, setIsRightActionBarVisible] = useState(false)
  const [showLeftInlineMarkers, setShowLeftInlineMarkers] = useState(
    leftThreads.length > 0 || leftAnnotations.length > 0,
  )
  const [showRightInlineMarkers, setShowRightInlineMarkers] = useState(
    rightThreads.length > 0 || rightAnnotations.length > 0,
  )
  const hasLeftThreads = leftThreads.length + leftAnnotations.length !== 0
  const hasRightThreads = rightThreads.length + rightAnnotations.length !== 0
  const leftCommentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasLeftThreads && commentsPreference === CommentsPreference.Collapsed,
  })
  const rightCommentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasRightThreads && commentsPreference === CommentsPreference.Collapsed,
  })
  // TODO: This needs to be reworked in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const inlineMarkersRef = useRef<HTMLDivElement | null>(null)
  const cellRef = useRef<HTMLTableCellElement | null>(null)

  return (
    <tr className="diff-line-row">
      {isHunkRow ? (
        <td colSpan={4} className="flex-grow-0 bgColor-accent-muted">
          <div className="d-flex flex-row">
            <code className="diff-text-cell hunk">
              <VerifiedHTMLDiv className="diff-text-inner color-fg-muted" html={leftDiffLine.html as SafeHTMLString} />
            </code>
          </div>
        </td>
      ) : (
        <>
          {!leftDiffLine ? (
            <>
              <td style={{userSelect: 'none', position: 'relative', backgroundColor: leftLineNumberBackgroundColor}} />
              <td style={{userSelect: 'none', position: 'relative', backgroundColor: leftLineNumberBackgroundColor}} />
            </>
          ) : (
            <>
              <td className="text-center v-align-top" style={{backgroundColor: leftLineNumberBackgroundColor}}>
                <code>{leftDiffLine.left}</code>
              </td>
              <td
                className="diff-text-cell"
                style={{backgroundColor: leftCodeBackgroundColor}}
                onMouseEnter={() => setIsLeftActionBarVisible(true)}
                onMouseLeave={() => setIsLeftActionBarVisible(false)}
              >
                <code
                  className={clsx('diff-text syntax-highlighted-line', {
                    addition: leftDiffLine.type === 'ADDITION',
                    deletion: leftDiffLine.type === 'DELETION',
                  })}
                >
                  {showLeftLineTypeCharacter && <span className="diff-text-marker">{leftLineTypeCharacter}</span>}
                  <VerifiedHTMLDiv html={leftLineHtml as SafeHTMLString} className="diff-text-inner" />
                </code>
                {showLeftInlineMarkers && (
                  <InlineMarkers
                    diffLine={leftDiffLine}
                    gridCellId={leftCellId}
                    gutterSizeOffset={leftCommentIndicatorGutterSize}
                    inlineMarkersRef={inlineMarkersRef}
                    cellRef={cellRef}
                    fileAnchor={diffAnchor}
                    // passing a noop because in simplified view we are not dealing with collapsed comments
                    dispatchMarkersStatus={noop}
                    lineMarkersKey={leftLineMarkersKey as LineKey}
                    annotations={leftAnnotations}
                    conversationListThreads={leftThreads}
                    filePath={filePath}
                    onCloseFocusMode={noop}
                    isRowSelected={false}
                    lineType={leftDiffLine.type}
                    onCloseConversationList={noop}
                    onDeleteLineComment={noop}
                    viewerData={viewerData as ViewerData}
                  >
                    {isLeftNewConversationDialogOpen && (
                      <div className={clsx(' border rounded-2 color-border-default', 'mt-2 mb-1')}>
                        <StartConversation
                          diffLine={leftDiffLine}
                          diffAnchor={diffAnchor}
                          batchingEnabled
                          filePath={filePath}
                          focusOnMount={false}
                          isLeftSide
                          isInDialogMode={false}
                          isOpen
                          lineNumber={leftDiffLine.left ?? undefined}
                          viewerData={viewerData}
                          onCloseCommentDialog={() => setIsLeftNewConversationDialogOpen(false)}
                        />
                      </div>
                    )}
                  </InlineMarkers>
                )}
                {isLeftActionBarVisible && (
                  <SimpleActionBar
                    openStartConversationDialog={() => {
                      setIsLeftNewConversationDialogOpen(true)
                      setShowLeftInlineMarkers(true)
                    }}
                    authorAvatarUrl={'undefined'}
                    authorLogin={'undefined'}
                    ref={cellRef as unknown as React.RefObject<HTMLButtonElement>}
                    copilotChatReference={undefined}
                    currentHunk={undefined}
                    diffAnchor={diffAnchor}
                    diffLine={leftDiffLine}
                    threads={leftThreads}
                    annotations={leftAnnotations}
                    handleCopyCode={noop}
                    hasDraftComment={false}
                    isLeftSide
                    isSplit={false}
                    nextHunk={undefined}
                    previousHunk={undefined}
                    //TODO: make this actually read a user's comment preference
                    showStartConversation
                    shouldDisplayMinimizedComments={false}
                    shouldDisplayCollapseComments
                    toggleViewingMarkers={noop}
                    viewerData={{
                      avatarUrl: 'string',
                      diffViewPreference: 'string',
                      lineSpacingPreference: 'compact',
                      login: 'string',
                      shouldDisplayComments: true,
                      shouldDisplayEditOptions: true,
                      tabSizePreference: 8,
                      viewerCanComment: true,
                      viewerCanApplySuggestion: true,
                    }}
                  />
                )}
              </td>
            </>
          )}

          {!rightDiffLine ? (
            <>
              <td
                style={{
                  userSelect: 'none',
                  position: 'relative',
                  backgroundColor: rightLineNumberBackgroundColor,
                }}
              />
              <td
                style={{
                  userSelect: 'none',
                  position: 'relative',
                  backgroundColor: rightLineNumberBackgroundColor,
                }}
              />
            </>
          ) : (
            <>
              <td className="text-center v-align-top" style={{backgroundColor: rightLineNumberBackgroundColor}}>
                <code>{rightDiffLine.right}</code>
              </td>
              <td
                className="diff-text-cell"
                style={{backgroundColor: rightCodeBackgroundColor}}
                onMouseEnter={() => setIsRightActionBarVisible(true)}
                onMouseLeave={() => setIsRightActionBarVisible(false)}
              >
                <code
                  className={clsx('diff-text syntax-highlighted-line', {
                    addition: rightDiffLine.type === 'ADDITION',
                    deletion: rightDiffLine.type === 'DELETION',
                  })}
                >
                  {showRightLineTypeCharacter && <span className="diff-text-marker">{rightLineTypeCharacter}</span>}
                  <VerifiedHTMLDiv html={rightLineHtml as SafeHTMLString} className="diff-text-inner" />
                </code>
                {showRightInlineMarkers && (
                  <InlineMarkers
                    diffLine={rightDiffLine}
                    gridCellId={rightCellId}
                    gutterSizeOffset={rightCommentIndicatorGutterSize}
                    inlineMarkersRef={inlineMarkersRef}
                    cellRef={cellRef}
                    fileAnchor={diffAnchor}
                    // passing a noop because in simplified view we are not dealing with collapsed comments
                    dispatchMarkersStatus={noop}
                    lineMarkersKey={rightLineMarkersKey as LineKey}
                    annotations={rightAnnotations}
                    conversationListThreads={rightThreads}
                    filePath={filePath}
                    viewerData={viewerData as ViewerData}
                    onCloseFocusMode={noop}
                    isRowSelected={false}
                    lineType={rightDiffLine.type}
                    onCloseConversationList={noop}
                    onDeleteLineComment={noop}
                  >
                    {isRightNewConversationDialogOpen && (
                      <div className={clsx(' border rounded-2 color-border-default', 'mt-2 mb-1')}>
                        <StartConversation
                          diffLine={rightDiffLine}
                          diffAnchor={diffAnchor}
                          batchingEnabled
                          filePath={filePath}
                          focusOnMount={false}
                          isLeftSide={false}
                          isInDialogMode={false}
                          isOpen
                          lineNumber={rightDiffLine.right ?? undefined}
                          viewerData={{
                            avatarUrl: avatarURL ?? '',
                            login: userLogin ?? '',
                          }}
                          onCloseCommentDialog={() => setIsRightNewConversationDialogOpen(false)}
                        />
                      </div>
                    )}
                  </InlineMarkers>
                )}
                {isRightActionBarVisible && (
                  <SimpleActionBar
                    openStartConversationDialog={() => {
                      setIsRightNewConversationDialogOpen(true)
                      setShowRightInlineMarkers(true)
                    }}
                    authorAvatarUrl={'undefined'}
                    authorLogin={'undefined'}
                    ref={cellRef as unknown as React.RefObject<HTMLButtonElement>}
                    copilotChatReference={undefined}
                    currentHunk={undefined}
                    diffAnchor={'diff-blah'}
                    diffLine={rightDiffLine}
                    threads={rightThreads}
                    annotations={rightAnnotations}
                    handleCopyCode={noop}
                    hasDraftComment={false}
                    isLeftSide={false}
                    isSplit={false}
                    nextHunk={undefined}
                    previousHunk={undefined}
                    //TODO: make this actually read a user's comment preference
                    showStartConversation
                    shouldDisplayMinimizedComments={false}
                    shouldDisplayCollapseComments
                    toggleViewingMarkers={noop}
                    viewerData={{
                      avatarUrl: 'string',
                      diffViewPreference: 'string',
                      lineSpacingPreference: 'compact',
                      login: 'string',
                      shouldDisplayComments: true,
                      shouldDisplayEditOptions: true,
                      tabSizePreference: 8,
                      viewerCanComment: true,
                      viewerCanApplySuggestion: true,
                    }}
                  />
                )}
              </td>
            </>
          )}
        </>
      )}
    </tr>
  )
}

try{ SimpleSplitDiffRow.displayName ||= 'SimpleSplitDiffRow' } catch {}